import { useQuery }     from '@tanstack/react-query';
import { useNavigate }  from '@tanstack/react-router';
import { Building2, FileText, Mail, PackageIcon, Search, X } from 'lucide-react';
import { useEffect, useState, useCallback, useMemo } from 'react';

import {
  Dialog, DialogContent, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { companiesApi } from '@/lib/api';
import { QK }          from '@/lib/query-client';
import { cn }          from '@/lib/utils';
import { useAppStore }  from '@/store/app.store';
import type { CompanyDto } from '@/types/api';

const MODULES = [
  { label: 'Declaraciones y Pagos', path: '/declaraciones', icon: FileText },
  { label: 'Buzón SUNAT',           path: '/buzon',         icon: Mail },
  { label: 'Paquetes de servicios', path: '/paquetes',      icon: PackageIcon },
] as const;

export function CommandSearch() {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { activeTenantId, setActiveCompany } = useAppStore();

  // Keyboard shortcut Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => { document.removeEventListener('keydown', handler); };
  }, []);

  // Reset query on close
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  // Companies (use cache from app-layout)
  const { data: companies = [] } = useQuery({
    queryKey: [...QK.companies(), activeTenantId],
    queryFn:  () => companiesApi.list().then(r => r.data),
    enabled:  !!activeTenantId,
    staleTime: Infinity,
  });

  const q = query.trim().toLowerCase();

  const filteredCompanies = useMemo(() => {
    if (!q) return companies.slice(0, 8);
    return companies.filter(c =>
      c.razonSocial.toLowerCase().includes(q) || c.ruc.includes(q)
    ).slice(0, 8);
  }, [companies, q]);

  const filteredModules = useMemo(() => {
    if (!q) return MODULES;
    return MODULES.filter(m => m.label.toLowerCase().includes(q));
  }, [q]);

  const runCompany = useCallback((company: CompanyDto) => {
    setActiveCompany(company.id);
    void navigate({ to: '/dashboard' });
    setOpen(false);
  }, [setActiveCompany, navigate]);

  const runModule = useCallback((path: string) => {
    void navigate({ to: path as '/declaraciones' });
    setOpen(false);
  }, [navigate]);

  const hasResults = filteredCompanies.length > 0 || filteredModules.length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Búsqueda rápida</DialogTitle>
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            autoFocus
            value={query}
            onChange={e => { setQuery(e.target.value); }}
            placeholder="Buscar empresa, módulo..."
            className="border-0 p-0 h-auto text-base focus-visible:ring-0 shadow-none bg-transparent"
          />
          {query && (
            <button onClick={() => { setQuery(''); }} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto py-2">
          {!hasResults && (
            <p className="text-sm text-muted-foreground text-center py-6">Sin resultados para &quot;{query}&quot;</p>
          )}

          {/* Companies */}
          {filteredCompanies.length > 0 && (
            <div>
              <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Empresas</p>
              {filteredCompanies.map(c => (
                <button
                  key={c.id}
                  onClick={() => { runCompany(c); }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-left',
                    'hover:bg-accent hover:text-accent-foreground transition-colors',
                  )}
                >
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{c.razonSocial}</span>
                  </span>
                  <span className="text-xs text-muted-foreground font-mono shrink-0">{c.ruc}</span>
                </button>
              ))}
            </div>
          )}

          {/* Modules */}
          {filteredModules.length > 0 && (
            <div>
              <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Módulos</p>
              {filteredModules.map(item => (
                <button
                  key={item.path}
                  onClick={() => { runModule(item.path); }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-left',
                    'hover:bg-accent hover:text-accent-foreground transition-colors',
                  )}
                >
                  <item.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t px-4 py-2 flex items-center gap-3 text-[11px] text-muted-foreground">
          <span>↑↓ navegar</span>
          <span>↵ seleccionar</span>
          <span>esc cerrar</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
